import { DateTime, Duration } from 'luxon';

import { type ApolloClient } from '@apollo/client';

import {
  AddressFragment,
  DoorToDoor__BundleEnum,
  DraftDocument,
  DraftFragment,
  DraftInput,
  DraftSaveAndNotifyDocument,
  DraftSaveAndNotifyMutation,
  DraftSaveAndNotifyMutationVariables,
  DraftUpsertDocument,
  DraftUpsertMutation,
  DraftUpsertMutationVariables,
  Draft__Address,
  Draft__MovingAddress,
  Lead__ServiceNeeds,
  PricingSetFragment,
  Status,
} from '@graphql/platform';

import { RATE_GROUP_COMMITMENT } from '@root/constants/pricing';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';
import { PlanName } from '@root/resources/types/plan_name';

import { geocode } from '@utils/geocode';
import { extractPlan, extractRateGroup } from '@utils/pricing/pricing_set';
import { ProtectionPlanEnum } from '@utils/protection_plan';

import { StorageCheckoutData } from '../data';
import { CheckoutVersion } from '../types';
import { InitialValues } from './initial_values/types';
import stringify from 'fast-json-stable-stringify';

let lastStorageDraft: string | undefined;

const CHALLENGER_DRAFT_UPSERT_DEBOUNCE_MS = 750;

type PendingChallengerDraftUpsert = {
  client: ApolloClient;
  input: DraftInput;
  serialized: string;
};

let challengerDraftUpsertTimeout: ReturnType<typeof setTimeout> | undefined;
let pendingChallengerDraftUpsert: PendingChallengerDraftUpsert | undefined;

const discardPendingStorageDraftUpsert = () => {
  if (challengerDraftUpsertTimeout) {
    clearTimeout(challengerDraftUpsertTimeout);
    challengerDraftUpsertTimeout = undefined;
  }
  pendingChallengerDraftUpsert = undefined;
};

const commitStorageDraftUpsert = (
  client: ApolloClient,
  input: DraftInput,
  serialized: string,
) => {
  if (serialized === lastStorageDraft) return;
  lastStorageDraft = serialized;
  upsertDraft(client, input);
};

/** Commit debounced challenger draft before leaving the funnel (same-tab resume). */
export const flushPendingStorageDraftUpsert = () => {
  if (challengerDraftUpsertTimeout) {
    clearTimeout(challengerDraftUpsertTimeout);
    challengerDraftUpsertTimeout = undefined;
  }
  const pending = pendingChallengerDraftUpsert;
  pendingChallengerDraftUpsert = undefined;
  if (pending) {
    commitStorageDraftUpsert(pending.client, pending.input, pending.serialized);
  }
};

function resolveDatePreferred(dateString?: string) {
  if (!dateString) return undefined;
  const parsed = DateTime.fromISO(dateString);
  return parsed.toMillis() > Date.now() ? parsed : undefined;
}

function formatRestoredWindow(from: DateTime, till: DateTime) {
  return `${from.toFormat('h:mm a')} - ${till.toFormat('h:mm a')}`;
}

function resolveDateScheduled(
  draft: DraftFragment,
): StorageCheckoutData['dateScheduled'] | undefined {
  if (!draft.preferredTime || !draft.slaWindowDuration) return undefined;
  const from = DateTime.fromISO(draft.preferredTime);
  if (!from.isValid || from.toMillis() <= Date.now()) return undefined;
  const duration = Duration.fromISO(draft.slaWindowDuration);
  const till = duration.isValid ? from.plus(duration) : from;
  const tillTime = till.toISO();
  if (!tillTime) return undefined;
  return {
    fromTime: draft.preferredTime,
    tillTime,
    duration: draft.slaWindowDuration,
    formatted: formatRestoredWindow(from, till),
    feeAmount: draft.timeSlotFeeAmount
      ? {
          currency: draft.timeSlotFeeAmount.currency,
          value: draft.timeSlotFeeAmount.value,
          formatted: draft.timeSlotFeeAmount.formatted,
        }
      : null,
  };
}

function convertAddress(
  address: Draft__Address | Draft__MovingAddress,
): Partial<AddressFragment> {
  return {
    street: address.street ?? undefined,
    aptsuite: address.aptsuite ?? undefined,
    zip: address.zip ?? undefined,
    city: address.city ?? undefined,
    state: address.state ?? undefined,
    latitude: address.latitude ?? undefined,
    longitude: address.longitude ?? undefined,
  };
}

function extractStorageData(draft: DraftFragment) {
  const data: Partial<InitialValues['storageData']> = {};

  if (draft.address) {
    data.address = convertAddress(draft.address);
  }

  if (draft.rateGroup) {
    data.commitment = RATE_GROUP_COMMITMENT[draft.rateGroup.name];
  }

  if (draft.plan?.name) {
    data.planSize = draft.plan.name as PlanName;
  }

  if (draft.protectionPlanSlug) {
    data.protectionPlan = draft.protectionPlanSlug as ProtectionPlanEnum;
  }

  if (draft.bundleKind) {
    data.bundleKind = draft.bundleKind;
  } else if (draft.packingHelpNeeded === true) {
    data.bundleKind = DoorToDoor__BundleEnum.PickupAndPacking;
  } else if (draft.packingHelpNeeded === false) {
    data.bundleKind = DoorToDoor__BundleEnum.Basic;
  }

  if (draft.disposalServiceNeeded != null) {
    data.disposalSelected = draft.disposalServiceNeeded;
  }

  if (draft.disposalItems?.length) {
    data.disposalItems = draft.disposalItems.map((item) => ({
      categoryID: item.category.id,
      quantity: item.quantity,
      categoryName: item.category.name,
      disposalCategoryGroupKind: item.category.disposalCategoryGroupKind,
    }));
  }

  const dateScheduled = resolveDateScheduled(draft);
  if (dateScheduled) {
    data.dateScheduled = dateScheduled;
  }

  return data;
}

const buildStorageDraftValues = (
  data: StorageCheckoutData,
  pricingSet: PricingSetFragment,
  funnelVersion: string = CheckoutVersion.Storage,
) => {
  const {
    zip,
    address,
    planSize,
    commitment,
    dateScheduled,
    protectionPlan,
    bundleKind,
    disposalSelected,
    disposalItems,
  } = data;

  const draftValues: Partial<DeepMutable<DraftInput>> = {};

  if (zip) {
    draftValues.address = {
      zip: address?.zip || zip,
      street: address?.street,
      city: address?.city,
      state: address?.state,
      aptsuite: address?.aptsuite,
      ...(funnelVersion === CheckoutVersion.StorageV2
        ? {
            latitude: address?.latitude ?? undefined,
            longitude: address?.longitude ?? undefined,
          }
        : {}),
    };
  }

  if (planSize && commitment && pricingSet) {
    draftValues.storagePlanID = extractPlan(
      commitment,
      planSize,
      pricingSet,
    )?.id;
  }

  if (commitment) {
    draftValues.rateGroupID = extractRateGroup(commitment, pricingSet)?.id;
  }

  if (dateScheduled) {
    draftValues.preferredTime = dateScheduled.fromTime;
    if (dateScheduled.duration) {
      draftValues.slaWindowDuration = dateScheduled.duration;
    }
    if (dateScheduled.feeAmount) {
      draftValues.timeSlotFeeAmount = {
        currency: dateScheduled.feeAmount.currency,
        value: dateScheduled.feeAmount.value,
      };
    }
  }

  if (protectionPlan) {
    draftValues.protectionPlanSlug = protectionPlan;
  }

  if (bundleKind) {
    draftValues.bundleKind = bundleKind;
    draftValues.packingHelpNeeded =
      bundleKind === DoorToDoor__BundleEnum.PickupAndPacking;
  }

  if (disposalSelected !== undefined) {
    draftValues.disposalServiceNeeded = disposalSelected;
  }

  if (disposalItems?.length) {
    draftValues.disposalItems = disposalItems.map((item) => ({
      categoryID: item.categoryID,
      quantity: item.quantity,
    }));
  }

  if (Object.keys(draftValues).length !== 0) {
    draftValues.latestService = Lead__ServiceNeeds.SmartStorage;
    draftValues.funnelVersion = funnelVersion;
    return draftValues;
  }
};

const upsertDraft = (client: ApolloClient, input: DraftInput) => {
  client
    .mutate<DraftUpsertMutation, DraftUpsertMutationVariables>({
      mutation: DraftUpsertDocument,
      variables: {
        input,
      },
    })
    .then((result) => {
      if (result.data?.draftUpsert?.draft) {
        client.writeQuery({
          data: { draft: result.data?.draftUpsert?.draft },
          query: DraftDocument,
        });
      }
    });
};

export const updateStorageDraft = (
  client: ApolloClient,
  data: StorageCheckoutData,
  leadToken?: string,
  pricingSet?: PricingSetFragment,
  funnelVersion?: string,
) => {
  if (!pricingSet) return;

  const resolvedFunnelVersion = funnelVersion ?? CheckoutVersion.Storage;
  const values = buildStorageDraftValues(
    data,
    pricingSet,
    resolvedFunnelVersion,
  );
  const stringified = values && stringify(values);

  // updateDraft may be called when non-draft values have changed, so we guard
  // against noop API calls
  if (!values || !stringified) return;
  if (stringified === lastStorageDraft) {
    if (resolvedFunnelVersion === CheckoutVersion.StorageV2) {
      discardPendingStorageDraftUpsert();
    }
    return;
  }

  const input: DraftInput = {
    leadToken,
    visitorToken: WT_VISITOR_TOKEN,
    quoteID: pricingSet.quoteId,
    ...values,
  };

  if (resolvedFunnelVersion !== CheckoutVersion.StorageV2) {
    commitStorageDraftUpsert(client, input, stringified);
    return;
  }

  pendingChallengerDraftUpsert = { client, input, serialized: stringified };
  if (challengerDraftUpsertTimeout) {
    clearTimeout(challengerDraftUpsertTimeout);
  }
  challengerDraftUpsertTimeout = setTimeout(() => {
    challengerDraftUpsertTimeout = undefined;
    const pending = pendingChallengerDraftUpsert;
    pendingChallengerDraftUpsert = undefined;
    if (pending) {
      commitStorageDraftUpsert(
        pending.client,
        pending.input,
        pending.serialized,
      );
    }
  }, CHALLENGER_DRAFT_UPSERT_DEBOUNCE_MS);
};

/**
 * Persist the current storage checkout draft, attach the lead, and email a
 * resume link (DraftSaveAndNotify / QUOTE_SAVED).
 */
export const saveStorageDraftAndNotify = async (
  client: ApolloClient,
  data: StorageCheckoutData,
  leadToken: string,
  pricingSet: PricingSetFragment,
  funnelVersion?: string,
): Promise<{ ok: true } | { ok: false; error: string }> => {
  const resolvedFunnelVersion = funnelVersion ?? CheckoutVersion.Storage;
  if (resolvedFunnelVersion === CheckoutVersion.StorageV2) {
    discardPendingStorageDraftUpsert();
  }
  const values = buildStorageDraftValues(
    data,
    pricingSet,
    resolvedFunnelVersion,
  ) ?? {
    latestService: Lead__ServiceNeeds.SmartStorage,
    funnelVersion: resolvedFunnelVersion,
  };

  const upsertResult = await client.mutate<
    DraftUpsertMutation,
    DraftUpsertMutationVariables
  >({
    mutation: DraftUpsertDocument,
    variables: {
      input: {
        leadToken,
        visitorToken: WT_VISITOR_TOKEN,
        quoteID: pricingSet.quoteId,
        ...values,
      },
    },
  });

  const draft = upsertResult.data?.draftUpsert?.draft;
  if (!draft?.id) {
    return {
      ok: false,
      error: 'Unable to save your progress. Please try again.',
    };
  }

  lastStorageDraft = stringify(values);
  client.writeQuery({
    data: { draft },
    query: DraftDocument,
  });

  const notifyResult = await client.mutate<
    DraftSaveAndNotifyMutation,
    DraftSaveAndNotifyMutationVariables
  >({
    mutation: DraftSaveAndNotifyDocument,
    variables: { id: draft.id },
  });

  const payload = notifyResult.data?.draftSaveAndNotify;
  if (payload?.status !== Status.Ok) {
    return {
      ok: false,
      error:
        payload?.error ||
        'Unable to email your saved progress. Please try again.',
    };
  }

  return { ok: true };
};

export const parseDraftToValues = async (
  client: ApolloClient,
  draft: DraftFragment,
) => {
  const storageZip = draft?.address?.zip ?? undefined;

  const sharedProperties = {
    datePreferred: resolveDatePreferred(draft?.preferredTime ?? undefined),
    leadToken: draft?.lead?.token,
    email: draft?.lead?.email ?? undefined,
    name: draft.lead?.name ?? undefined,
    phone: draft.lead?.phone ?? undefined,
  };

  const geocodeResult = storageZip
    ? (await geocode(client, storageZip))?.geocode
    : undefined;

  const storageData: Omit<InitialValues['storageData'], 'checkoutType'> = {
    ...sharedProperties,
    ...(draft ? extractStorageData(draft) : {}),
    zip: storageZip,
    zipValidated: geocodeResult?.eligibleForDoorToDoor,
  };

  return {
    storageData,
  };
};
